
import { fetchIntelliBoostXml } from './apiActions';

/**
 * @fileoverview Utility functions for Click&Pledge donation processing.
 * Contains helper functions for recurring payments, amount calculations, date handling,
 * XML formatting, and various donation-related operations.
 */

/**
 * Converts recurring key to display value.
 * Maps internal checkbox keys to user-friendly recurring period names.
 *
 * @param {string} key - Internal recurring key (e.g., "chkMonth")
 * @returns {string} User-friendly recurring period name (e.g., "Month")
 */
export const getRecurringKeyToValue = key => {
    switch (key) {
    case 'chkOneTimeOnly':
        return 'One Time Only';
    case 'chkWeek':
        return 'Week';
    case 'chk2Weeks':
        return '2 Weeks';
    case 'chkMonth':
        return 'Month';
    case 'chk2Months':
        return '2 Months';
    case 'chkQuarter':
        return 'Quarter';
    case 'chk6Months':
        return '6 Months';
    case 'chkYear':
        return 'Year';
    default:
        return key;
    }
};

/**
 * Converts recurring value to internal key.
 * Maps user-friendly recurring period names to internal checkbox keys.
 *
 * @param {string} val - User-friendly recurring period name (e.g., "Month")
 * @returns {string} Internal recurring key (e.g., "chkMonth")
 */
export const getRecurringValueToKey = val => {
    switch (val) {
    case 'One Time Only':
        return 'chkOneTimeOnly';
    case 'Week':
        return 'chkWeek';
    case '2 Weeks':
        return 'chk2Weeks';
    case 'Month':
        return 'chkMonth';
    case '2 Months':
        return 'chk2Months';
    case 'Quarter':
        return 'chkQuarter';
    case '6 Months':
        return 'chk6Months';
    case 'Year':
        return 'chkYear';

    default:
        return 'chkOneTimeOnly';
    }
};

/**
 * Converts recurring display value to API format for XML Periodicity field.
 * Maps user-friendly names to API-expected values that the backend recognizes.
 *
 * @param {string} val - User-friendly recurring period name (e.g., "Month")
 * @returns {string} API-expected value for Periodicity (e.g., "Monthly")
 */
export const getRecurringValueToApiFormat = val => {
    switch (val) {
    case 'One Time Only':
        return 'None';
    case 'Week':
        return 'Weekly';
    case '2 Weeks':
        return 'Bi-Weekly';
    case 'Month':
        return 'Monthly';
    case '2 Months':
        return 'Bi-Monthly';
    case 'Quarter':
        return 'Quarterly';
    case '6 Months':
        return 'Semi-Annually';
    case 'Year':
        return 'Yearly';
    default:
        return 'None';
    }
};

/**
 * Calculates the payment amount based on various factors.
 * Handles installments, convenience fees, and recurring payment methods.
 *
 * @param {number} amount - Base donation amount
 * @param {number} installment - Number of installments
 * @param {number} convFee - Convenience fee amount
 * @param {boolean} IsConvFeeSelected - Whether convenience fee is selected
 * @param {string} recurringMethod - Recurring payment method type
 * @returns {number} Calculated payment amount
 */
export const getPaymentAmount = (amount, installment, convFee, ...rest) => {
    const [ IsConvFeeSelected, recurringMethod ] = rest;

    if (amount <= 0 && !IsConvFeeSelected) {
        return amount;
    } else if (amount <= 0 && IsConvFeeSelected) {
        return parseFloat(convFee);
    } else if (amount > 0 && installment && !IsConvFeeSelected && recurringMethod && recurringMethod !== 'Subscription') {
        return getDecimalPoint(amount, installment, '');
    } else if (amount > 0 && !installment && IsConvFeeSelected) {
        return getDecimalPoint(amount, '', convFee);
    } else if (amount > 0 && installment && IsConvFeeSelected && recurringMethod && recurringMethod !== 'Subscription') {
        return getDecimalPoint(amount, installment, convFee);
    } else if (amount > 0 && installment && IsConvFeeSelected && recurringMethod && recurringMethod === 'Subscription') {
        return getDecimalPoint(amount, '', convFee);
    } else if (amount > 0 && installment && !IsConvFeeSelected && recurringMethod && recurringMethod === 'Subscription') {
        return parseFloat(amount);
    }

    return parseFloat(amount);

};


/**
 * Counts the number of decimal places in a number.
 *
 * @param {number} num - Number to count decimal places for
 * @returns {number} Number of decimal places
 */
export const countNoOfDecimals = num => {
    // Check if it's a number and has a decimal point
    if (Math.floor(num) !== num && !isNaN(num)) {
        return num.toString().split('.')[1].length;
    }

    return 0;
};

/**
 * Calculates amount with proper decimal precision.
 * Handles installment division and convenience fee addition.
 *
 * @param {number} amount - Base amount
 * @param {number} installments - Number of installments (optional)
 * @param {number} convFee - Convenience fee (optional)
 * @returns {number} Calculated amount with proper decimal precision
 */
export function getDecimalPoint(amount, installments, convFee) {
    let amt = countNoOfDecimals(parseFloat(amount)) > 2 ? parseFloat(removedecimal(amount)) : parseFloat(amount);

    if (isNaN(amt)) {
        amt = 0;
    }

    let addfee = countNoOfDecimals(parseFloat(convFee)) > 2 ? parseFloat(removedecimal(convFee)) : parseFloat(convFee);

    if (isNaN(addfee)) {
        addfee = 0;
    }

    const parsedInstallments = parseFloat(installments);

    if (parsedInstallments && !isNaN(parsedInstallments)) {
        amt = Math.round(((amt / parsedInstallments) + Number.EPSILON) * 100) / 100;
    }

    if (convFee) {
        amt = Math.round(((amt + addfee) + Number.EPSILON) * 100) / 100;
    }

    if (isNaN(amt)) {
        amt = 0;
    }
    amt = parseFloat(removedecimal(amt));

    return amt;
}

/**
 * Removes fee from amount with proper decimal handling.
 *
 * @param {number} amount - Base amount
 * @param {number} fee - Fee to remove
 * @returns {number} Amount with fee removed
 */
export const getRemovedFee = (amount, fee) => {
    let amt = countNoOfDecimals(parseFloat(amount)) > 2 ? parseFloat(removedecimal(amount)) : parseFloat(amount);

    if (isNaN(amt)) {
        amt = 0;
    }

    let addfee = countNoOfDecimals(parseFloat(fee)) > 2 ? parseFloat(removedecimal(fee)) : parseFloat(fee);

    if (isNaN(addfee)) {
        addfee = 0;
    }

    if (fee) {
        amt = Math.round(((amt - addfee) + Number.EPSILON) * 100) / 100;
    }

    if (isNaN(amt)) {
        amt = 0;
    }
    amt = parseFloat(removedecimal(amt));

    return amt;
};

/**
 * Calculates tax deductible amount based on percentage and Fair Market Value.
 *
 * @param {number} amount - Base donation amount
 * @param {number} tax - Tax deductible percentage
 * @param {number} FMV - Fair Market Value
 * @returns {number} Tax deductible amount
 */
export const getTaxDeductableAmount = (amount, tax, FMV) => {

    let itemTaxDeductable = countNoOfDecimals(parseFloat(amount)) > 2 ? parseFloat(removedecimal(amount)) : parseFloat(amount);

    if (tax > 0) {
        itemTaxDeductable = (itemTaxDeductable * tax) / 100;
    }
    if (tax === 0) {
        itemTaxDeductable = 0;
    }
    itemTaxDeductable = parseFloat(removedecimal(itemTaxDeductable));

    // FMV (Fare Market Value) Calculation
    const item_FMV = FMV && typeof FMV === 'string' ? parseFloat(removedecimal(FMV)) : FMV;

    if (item_FMV > 0) {
        const itemFMV = (amount - item_FMV) < 0 ? 0 : amount - item_FMV;

        itemTaxDeductable = itemTaxDeductable + itemFMV;
        itemTaxDeductable = itemTaxDeductable > amount ? amount : itemTaxDeductable;
    }

    return itemTaxDeductable;
};


/**
 * Removes excess decimal places from a number, keeping only 2 decimal places.
 *
 * @param {number} itemvalue - Number to format
 * @returns {number} Number with maximum 2 decimal places
 */
export function removedecimal(itemvalue) {
    if (itemvalue === null || itemvalue === undefined || (isNaN(itemvalue) && typeof itemvalue !== 'string')) {
        return itemvalue;
    }

    const value = itemvalue.toString();

    if (value.indexOf('.') !== -1) {
        const substr = value.split('.');

        if (substr[1].length > 2) {
            return `${substr[0]}.${substr[1].substr(0, 2)}`;
        }
    }

    return value;
}

/**
 * Replaces multiple strings in a text using a mapping object.
 *
 * @param {string} str - String to perform replacements on
 * @param {Object} mapObj - Object mapping search strings to replacement strings
 * @returns {string} String with replacements applied
 */
export const replaceAll = (str, mapObj) => {
    const re = new RegExp(Object.keys(mapObj).join('|'), 'gi');

    return str.replace(re, matched => mapObj[matched]);

};

/**
 * Parses button style settings from a semicolon-separated string.
 *
 * @param {string} settings - Style settings string (e.g., "color:red;font-size:12px")
 * @returns {Object|null} Object with style properties or null if invalid
 */
export const getButtonStyles = settings => {
    if (settings) {
        const x = settings.split(';');

        const obj = {};

        for (let i = 0; i < x.length; i++) {
            const split = x[i].split(':');

            if (split[0].includes(' ')) {
                obj[split[0].trim()] = split[1].trim();
            } else {
                obj[split[0]] = split[1];
            }

        }

        return obj;
    }

    return null;


};

/**
 * Converts row name to Bootstrap column value.
 *
 * @param {string} row - Row name ("one", "two", "three", "four")
 * @returns {number} Bootstrap column value
 */
export const getButtonRowVal = row => {
    let rowval = 0;

    switch (row) {
    case 'one':
        rowval = 12;
        break;
    case 'two':
        rowval = 2;
        break;
    case 'three':
        rowval = 3;
        break;
    case 'four':
        rowval = 4;
        break;
    default:
        rowval = 4;
    }

    return rowval;
};


/**
 * Calculates convenience fee based on donation amount and fee parameters.
 *
 * @param {number} D - Donation amount
 * @param {number} rate - Fee rate percentage
 * @param {number} P - Fixed fee amount
 * @param {number} installment - Number of installments
 * @param {string} periodicity - Payment periodicity
 * @param {boolean} EN - Whether fee is enabled
 * @param {string} recMethod - Recurring method
 * @returns {number} Calculated convenience fee
 */
export const CalcConvenienceFee = (D, rate, P, ...rest) => {
    const [ , , EN ] = rest;

    const parsedD = parseFloat(D);

    if (isNaN(parsedD)) {
        return 0;
    }

    let F = 0;
    let R = parseFloat(rate) / 100;

    if (isNaN(R)) {
        R = 0;
    }

    let parsedP = parseFloat(P);

    if (isNaN(parsedP)) {
        parsedP = 0;
    }

    if (R >= 1) {
        throw new Error('Discount rate must be less than 1.');
    }
    if (EN) {
        F = ((parsedD * R) + parsedP) / (1 - R);
    } else {
        F = (parsedD * R) + parsedP;
    }

    F = F + 0.005;

    const finalFee = parseFloat(parseInt(F * 100, 10) / 100);

    return isNaN(finalFee) ? 0 : finalFee;

};

/**
 * Calculates tax deductible amount for convenience fee.
 *
 * @param {number} conFeeAmt - Convenience fee amount
 * @param {number} TaxDeductible - Tax deductible percentage
 * @param {number} FMV - Fair Market Value
 * @returns {number} Tax deductible amount for convenience fee
 */
export const confeeTaxDeductable = (conFeeAmt, TaxDeductible, FMV) => {
    let taxDeductable = parseFloat(removedecimal((conFeeAmt * TaxDeductible) / 100));
    const confee_FMV = FMV && typeof FMV === 'string' ? parseFloat(removedecimal(FMV)) : FMV;
    const conFee_Amt = conFeeAmt && typeof conFeeAmt === 'string' ? parseFloat(removedecimal(conFeeAmt)) : conFeeAmt;

    if (confee_FMV > 0) {
        const fmvValue = (conFee_Amt - confee_FMV) < 0 ? 0 : conFee_Amt - confee_FMV;
        const totalDeductible = taxDeductable + fmvValue;

        taxDeductable = totalDeductible > conFee_Amt ? conFee_Amt : totalDeductible;
    }

    return taxDeductable;
};

/**
 * Performs recurring payment calculation validation.
 *
 * @param {number} txtvalue - Input value
 * @param {number} Installmentvalue - Maximum installment value
 * @returns {number|string} Valid installment value or 'false' if invalid
 */
function recCalculation(txtvalue, Installmentvalue) {

    if (txtvalue === 0) {
        return Installmentvalue;
    } else if (txtvalue <= Installmentvalue) {
        return txtvalue;
    } else if (txtvalue < 0) {
        return 'false';
    }

    return 'false';

}

/**
 * Gets today's date in MM/dd/yyyy format.
 *
 * @returns {string} Today's date formatted as MM/dd/yyyy
 */
function getTodayDate() {

    const tdate = new Date();

    const dd = tdate.getDate(); // yields day

    const MM = tdate.getMonth(); // yields month

    const yyyy = tdate.getFullYear(); // yields year

    const xxx = `${MM + 1}/${dd}/${yyyy}`;

    return xxx;

}

/**
 * Formats a date object to MM/dd/yyyy string.
 *
 * @param {Date} date - Date object to format
 * @returns {string} Formatted date string
 */
const getFormattedDate = date => {
    const year = date.getFullYear();
    const month = (1 + date.getMonth()).toString().padStart(2, '0');
    const day = date.getDate().toString()
        .padStart(2, '0');

    return `${month}/${day}/${year}`;
};

/**
 * Calculates the end period for recurring payments based on date range and periodicity.
 *
 * @param {string} recurringtype - Type of recurring payment ("installment" or "subscription")
 * @param {string} endDate - End date for the recurring payments
 * @param {string} period - Payment period (e.g., "week", "month", "year")
 * @returns {Object} Object with data and error properties
 */
export const calculateEndPeriod = (recurringtype, endDate, period) => {

    const transaction = getTodayDate();
    const en = getFormattedDate(new Date(endDate));
    let txtvalue = '';
    let limit = '';

    if (recurringtype === 'installment') {

        limit = '998';
    } else if (recurringtype === 'subscription') {

        limit = '999';
    }

    if (txtvalue === '') {
        txtvalue = 0;
    }

    let Installmentvalue = '';


    if (en !== '') {

        const t1 = transaction.split('/');
        const en1 = en.split('/');

        const tdate1 = new Date(t1[2], t1[0] - 1, t1[1]);
        const endate1 = new Date(en1[2], en1[0] - 1, en1[1]);

        const Diff = Math.ceil((endate1 - tdate1) / (1000 * 60 * 60 * 24));

        if (period.toLowerCase() === 'week') {

            if (Diff < 7) {

                return {
                    data: '',
                    error: 'Please select valid End Date at settings'
                };
            }
            if (txtvalue > limit) {
                return {
                    data: '',
                    error: `# of Installments should be greater than 1 and less than or equal to ${limit}`
                };
            }

            if (parseInt(Diff / 7, 10) < 1) {
                return {
                    data: '',
                    error: `# of Installments should be greater than 1 and less than or equal to ${limit}`
                };
            }

            Installmentvalue = parseInt(Diff / 7, 10) + 1;
            const returnvalue = recCalculation(txtvalue, Installmentvalue);

            if (returnvalue === 'false') {
                return {
                    data: '',
                    error: `# of Installments should not be greater than ${Installmentvalue}`
                };
            }

            return {
                data: returnvalue,
                error: ''
            };


        }

        if (period.toLowerCase() === '2 weeks') {

            if (Diff < 14) {

                return {
                    data: '',
                    error: 'Please set valid End Date at settings'
                };
            }
            if (txtvalue > limit) {
                return {
                    data: '',
                    error: `# of Installments should be greater than 1 and less than or equal to ${limit}`
                };
            }

            if (parseInt(Diff / 14, 10) < 1) {
                return {
                    data: '',
                    error: `# of Installments should be greater than 1 and less than or equal to ${limit}`
                };
            }

            Installmentvalue = parseInt(Diff / 14, 10) + 1;
            const returnvalue = recCalculation(txtvalue, Installmentvalue);

            if (returnvalue === 'false') {
                return {
                    data: '',
                    error: `# of Installments should not be greater than ${Installmentvalue}`
                };
            }

            return {
                data: returnvalue,
                error: ''
            };


        }
        if (period.toLowerCase() === 'month') {
            if (Diff < 30) {
                return {
                    data: '',
                    error: 'Please set valid End Date at settings'
                };
            }
            if (txtvalue > limit) {
                return {
                    data: '',
                    error: `# of Installments should be greater than 1 and less than or equal to ${limit}`
                };
            }

            if (parseInt(Diff / 30, 10) < 1) {
                return {
                    data: '',
                    error: `# of Installments should be greater than 1 and less than or equal to ${limit}`
                };
            }

            Installmentvalue = parseInt(Diff / 30, 10) + 1;
            const returnvalue = recCalculation(txtvalue, Installmentvalue);

            if (returnvalue === 'false') {
                return {
                    data: '',
                    error: `# of Installments should not be greater than ${Installmentvalue}`
                };

            }

            return {
                data: returnvalue,
                error: ''
            };


        }
        if (period.toLowerCase() === '2 months') {
            if (Diff < 61) {
                // ShowfbAlert('Please set valid End Date at settings');
                return {
                    data: '',
                    error: 'Please set valid End Date at settings'
                };
            }
            if (txtvalue > limit) {
                // ShowfbAlert('# of Installments should be greater than 1 and less than or equal to ' + limit);
                return {
                    data: '',
                    error: `# of Installments should be greater than 1 and less than or equal to ${limit}`
                };
            }

            if (parseInt(Diff / 61, 10) < 1) {
                return {
                    data: '',
                    error: `# of Installments should be greater than 1 and less than or equal to ${limit}`
                };
            }

            Installmentvalue = parseInt(Diff / 61, 10) + 1;
            const returnvalue = recCalculation(txtvalue, Installmentvalue);

            if (returnvalue === 'false') {
                return {
                    data: '',
                    error: `# of Installments should not be greater than ${Installmentvalue}`
                };
            }

            return {
                data: returnvalue,
                error: ''
            };


        }
        if (period.toLowerCase() === 'quarter') {
            if (Diff < 91) {
                return {
                    data: '',
                    error: 'Please set valid End Date at settings'
                };

            }
            if (txtvalue > limit) {
                return {
                    data: '',
                    error: `# of Installments should be greater than 1 and less than or equal to ${limit}`
                };
            }

            // if (parseInt(Diff / 91) > 999 || parseInt(Diff / 91) < 1) {
            if (parseInt(Diff / 91, 10) < 1) {
                return {
                    data: '',
                    error: `# of Installments should be greater than 1 and less than or equal to ${limit}`
                };
            }

            Installmentvalue = parseInt(Diff / 91, 10) + 1;
            const returnvalue = recCalculation(txtvalue, Installmentvalue);

            if (returnvalue === 'false') {
                return {
                    data: '',
                    error: `# of Installments should not be greater than ${Installmentvalue}`
                };
            }

            return {
                data: returnvalue,
                error: ''
            };


        }
        if (period.toLowerCase() === '6 months') {
            if (Diff < 183) {
                return {
                    data: '',
                    error: 'Please set valid End Date at settings'
                };
            }
            if (txtvalue > limit) {
                return {
                    data: '',
                    error: `# of Installments should be greater than 1 and less than or equal to ${limit}`
                };
            }

            // if (parseInt(Diff / 183) > 999 || parseInt(Diff / 183) < 1) {
            if (parseInt(Diff / 183, 10) < 1) {
                return {
                    data: '',
                    error: `# of Installments should be greater than 1 and less than or equal to ${limit}`
                };
            }

            Installmentvalue = parseInt(Diff / 183, 10) + 1;
            const returnvalue = recCalculation(txtvalue, Installmentvalue);

            if (returnvalue === 'false') {
                return {
                    data: '',
                    error: `# of Installments should not be greater than ${Installmentvalue}`
                };

            }

            return {
                data: returnvalue,
                error: ''
            };


        }
        if (period.toLowerCase() === 'year') {
            if (Diff < 365) {
                return {
                    data: '',
                    error: 'Please set valid End Date at settings'
                };

            }

            if (txtvalue > limit) {
                return {
                    data: '',
                    error: `# of Installments should be greater than 1 and less than or equal to ${limit}`
                };
            }

            // if (parseInt(Diff / 365) > 999 || parseInt(Diff / 365) < 1) {
            if (parseInt(Diff / 365, 10) < 1) {
                return {
                    data: '',
                    error: `# of Installments should be greater than 1 and less than or equal to ${limit}`
                };
            }

            Installmentvalue = parseInt(Diff / 365, 10) + 1;
            const returnvalue = recCalculation(txtvalue, Installmentvalue);

            if (returnvalue === 'false') {
                return {
                    data: '',
                    error: `# of Installments should not be greater than ${Installmentvalue}`
                };
            }

            return {
                data: returnvalue,
                error: ''
            };


        }


    }


};

// export const filterPeriodicityByEndDate = (endDate,period) =>{

//     let transaction = GetTodayDate();
//     let en = getFormattedDate(new Date(endDate));

//     if(en !== ''){
//         let t1 = transaction.split('/');
//         let en1 = en.split('/');

//         let tdate1 = new Date(t1[2], (t1[0] - 1), t1[1]);
//         let endate1 = new Date(en1[2], (en1[0] - 1), en1[1]);

//         let Diff = Math.ceil((endate1 - tdate1) / (1000 * 60 * 60 * 24));


//         if (period.toLowerCase() === 'week') {

//             if (Diff < 7) return false;
//             else return true;
//         }
//         if (period.toLowerCase() === '2 weeks') {

//             if (Diff < 14) return false;
//             else return true;
//         }
//         if (period.toLowerCase() === 'month') {
//             if (Diff < 30) return false;
//             else return true;
//         }
//         if (period.toLowerCase() === '2 months') {
//             if (Diff < 61) return false;
//             else return true;
//         }
//         if (period.toLowerCase() === 'quarter') {
//             if (Diff < 91) return false;
//             else return true;
//         }
//         if (period.toLowerCase() === '6 months') {
//             if (Diff < 183) return false;
//             else return true;
//         }
//         if (period.toLowerCase() === 'year') {
//             if (Diff < 365) return false;
//             else return true;
//         }
//     }
// }

/**
 * Filters periodicity options based on end date availability.
 * Determines if a given period is valid based on the time remaining until end date.
 *
 * @param {string} endDate - End date for validation
 * @param {string} period - Payment period to validate
 * @returns {boolean} True if period is valid for the given end date
 */
export const filterPeriodicityByEndDate = (endDate, period) => {
    if (!endDate) {
        return false;
    }

    const today = new Date();
    const end = new Date(endDate);

    if (end < today) {
        return false;
    } // already expired

    const periodLower = period.toLowerCase();

    let diffPeriods = 0;

    switch (periodLower) {
    case 'week': {
        const totalDays = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
        let weekBlocks = Math.floor(totalDays / 7);

        if (totalDays % 7 === 0) {
            weekBlocks += 1;
        } // exact multiple of 7 counts
        diffPeriods = weekBlocks;
        break;
    }
    case '2 weeks': {
        const totalDays = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
        let twoWeekBlocks = Math.floor(totalDays / 14);

        if (totalDays % 14 === 0) {
            twoWeekBlocks += 1;
        } // exact multiple of 14 counts
        diffPeriods = twoWeekBlocks;
        break;
    }
    case 'month': {
        const months
                = ((end.getFullYear() - today.getFullYear()) * 12)
                + (end.getMonth() - today.getMonth());

        diffPeriods = months;
        break;
    }
    case '2 months': {
        const months
                = ((end.getFullYear() - today.getFullYear()) * 12)
                + (end.getMonth() - today.getMonth());

        diffPeriods = Math.ceil(months / 2);
        break;
    }
    case 'quarter': {
        const months
                = ((end.getFullYear() - today.getFullYear()) * 12)
                + (end.getMonth() - today.getMonth());

        diffPeriods = Math.ceil(months / 3);
        break;
    }
    case '6 months': {
        const months
                = ((end.getFullYear() - today.getFullYear()) * 12)
                + (end.getMonth() - today.getMonth());

        diffPeriods = Math.ceil(months / 6);
        break;
    }
    case 'year': {
        let years = end.getFullYear() - today.getFullYear();


        // adjust if month/day hasn't reached yet in end year
        if (end.getMonth() > today.getMonth()) {
            years += 1;
        }
        diffPeriods = Math.floor(years / 1);
        break;
    }
    default:
        return false;
    }

    return diffPeriods > 1;
};

/**
 * Checks if a donation item has expired based on start and end dates.
 *
 * @param {string} startdate - Start date of the donation item
 * @param {string} enddate - End date of the donation item
 * @returns {boolean} True if the item has expired
 */
export const checkDateExpired = (startdate, enddate) => {

    let expired = false;
    const tDate = new Date();
    const tmonth = (tDate.getMonth() + 1).toString().padStart(2, '0');
    const tday = tDate.getDate().toString()
        .padStart(2, '0');
    const tyear = tDate.getFullYear();
    const todDate = `${tmonth}/${tday}/${tyear}`;
    const todayDate = new Date(todDate).getTime();

    const givenSDate = new Date(startdate);
    const gsmonth = (givenSDate.getMonth() + 1).toString().padStart(2, '0');
    const gsday = givenSDate.getDate().toString()
        .padStart(2, '0');
    const gsyear = givenSDate.getFullYear();
    const cmpSDate = `${gsmonth}/${gsday}/${gsyear}`;
    const CompareSDate = new Date(cmpSDate).getTime();

    const givenEDate = new Date(enddate);
    const gemonth = (givenEDate.getMonth() + 1).toString().padStart(2, '0');
    const geday = givenEDate.getDate().toString()
        .padStart(2, '0');
    const geyear = givenEDate.getFullYear();
    const cmpEDate = `${gemonth}/${geday}/${geyear}`;
    const CompareEDate = new Date(cmpEDate).getTime();

    if (startdate && CompareSDate > todayDate) {
        expired = true;
    }

    if (enddate && CompareEDate < todayDate) {
        expired = true;
    }

    if (startdate && enddate && CompareEDate < todayDate && CompareSDate > todayDate) {
        expired = true;
    }

    return expired;
};


/**
 * Calculates recurring lift values for donation amount suggestions.
 * Used for intelligent donation amount recommendations.
 *
 * @param {number} Lift_Base - Base lift amount
 * @param {number} lift - Lift percentage
 * @param {number} lift_Max_Start - Maximum start value
 * @param {number} Lift_Minimum - Minimum lift value
 * @param {number} Lift_Maximum - Maximum lift value
 * @param {number} Round_Base - Rounding base value
 * @returns {Object} Object with StartValue and Increment properties
 */
export const recurringLiftValue = (Lift_Base, lift, lift_Max_Start, ...rest) => {
    const [ Lift_Minimum, Lift_Maximum, Round_Base ] = rest;
    const campaignLiftValues = {};
    let liftBase = Lift_Base;
    let roundBase = Round_Base;

    // Calculate Lift_Base_new
    const Lift_Base_new = liftBase * (1 + (lift / 100));

    if (Lift_Base_new < Lift_Maximum) {
        liftBase = Lift_Base_new;
    }

    // Adjust Lift_Base within bounds
    if (liftBase < Lift_Minimum) {
        liftBase = Lift_Minimum;
    }

    const Lift = lift / 100;

    // Calculate Lift_Base_Digits_Count
    const Lift_Base_Digits_Count = Math.floor(Math.log10(liftBase)) + 1;

    if (Lift_Base_Digits_Count - roundBase < 0) {
        roundBase = 0;
    } else {
        roundBase = Lift_Base_Digits_Count - roundBase;
    }

    // Calculate Base and Remainder
    const Base = parseInt(liftBase / Math.pow(10, roundBase), 10);
    const Remainder = liftBase - (Base * Math.pow(10, roundBase));
    const Remainder_Multiple_5 = parseInt((Remainder / (5 * Math.pow(10, roundBase - 1))) + 0.5, 10);
    const Remainder_Rounded = Remainder_Multiple_5 * 5 * Math.pow(10, roundBase - 1);
    const Base_10 = Base * Math.pow(10, roundBase);
    let Start = parseInt(Base_10 + Remainder_Rounded, 10);

    // Adjust Start within bounds
    if (Start < Lift_Minimum) {
        Start = parseInt(Lift_Minimum, 10);
    }
    if (Start > Lift_Maximum) {
        Start = parseInt(Lift_Maximum, 10);
    }
    if (Start > lift_Max_Start) {
        Start = parseInt(lift_Max_Start, 10);
    }

    // Calculate Lift_Calculated and Lift_Adjusted
    const Lift_Calculated = liftBase * Lift;
    const Lift_Adjusted = Lift_Calculated + liftBase - Start;
    const Lift_Digits_Count = parseInt(Math.floor(Math.log10(Lift_Calculated)), 10);

    // Calculate Lift_Base_Round
    const Lift_Base_Round = Lift_Adjusted / Math.pow(10, Lift_Digits_Count);
    const Lift_Base_Round_Left = parseInt(Lift_Base_Round, 10);
    const Lift_Base_Round_Right = removedecimal(Lift_Base_Round - Lift_Base_Round_Left);

    // Calculate nearest multiple of 5
    const Lift_Base_Round_Right_Multiple_5 = parseFloat(Lift_Base_Round_Right) / 0.5;
    let Lift_Base_Round_Right_Nearest_5 = 0;

    if (Lift_Base_Round_Right_Multiple_5 <= 0.5) {
        Lift_Base_Round_Right_Nearest_5 = Math.floor(Lift_Base_Round_Right_Multiple_5) * 0.5;
    } else {
        Lift_Base_Round_Right_Nearest_5 = Math.round(Lift_Base_Round_Right_Multiple_5) * 0.5;
    }

    // Calculate Increment
    let Increment = (Lift_Base_Round_Left + Lift_Base_Round_Right_Nearest_5) * Math.pow(10, Lift_Digits_Count);

    Increment = parseInt(0.99 + (Increment / 10), 10) * 10;

    // Add values to campaignLiftValues object
    campaignLiftValues.StartValue = Start;
    campaignLiftValues.Increment = Increment;

    return campaignLiftValues;


};


/**
 * Generates boosted donation values based on lift calculations.
 *
 * @param {Array} paymentOptions - Array of payment options to boost
 * @param {Object} cookieData - Cookie data containing boost parameters
 * @returns {Array} Updated payment options with boosted values
 */
export const getBoostUpDonationValues = (paymentOptions, cookieData) => {

    const { StartValue, Increment, defaultSelection, maxAmountVal } = cookieData;

    // Loop through each payment option
    paymentOptions.forEach(paymentOption => {
        let currentValue = StartValue; // Initialize current value to StartValue

        if (paymentOption.Value.PaymentType === 'Single Payment') {

            // Filter DonationItemList to exclude items where the DefaultValue exceeds maxAmountVal
            paymentOption.Value.DonationItemList = paymentOption.Value.DonationItemList.filter((item, index, self) => {

                if (item.Type !== 'Open') {
                    const previousItem = self[index - 1];

                    item.DefaultValue = currentValue;
                    item.ItemAmount = currentValue;
                    item.DefaultValueDisplay = currentValue.toFixed(2);
                    item.DefaultSelection = parseInt(defaultSelection, 10) === index + 1;
                    currentValue += Increment ? parseInt(Increment, 10) : parseInt(maxAmountVal - currentValue, 10); // Increment the value for the next item
                    // If it's the last item in the list and the DefaultValue exceeds maxVal, set DefaultValue to maxVal
                    if (item.DefaultValue > maxAmountVal) {
                        item.DefaultValue = maxAmountVal;
                        item.ItemAmount = maxAmountVal;
                        item.DefaultValueDisplay = maxAmountVal.toFixed(2);
                    }


                    // Keep the item only if DefaultValue is less than or equal to maxAmountVal
                    return item.DefaultValue <= maxAmountVal && (!previousItem || previousItem.DefaultValue !== item.DefaultValue);
                }

                return true;
            });
        }
    });

    return paymentOptions;
};

/**
 * Generates lift-up donation suggestions based on seed amount and form settings.
 *
 * @param {number} seedAmt - Seed amount for lift calculations
 * @param {Object} formSettings - Form settings containing InteliGive configuration
 * @param {Array} amountsArray - Array of existing amounts
 * @returns {Object} Lift data object with calculated values
 */
export const getLiftUpDonations = (seedAmt, formSettings) => {

    const maxValue = formSettings.InteliGiveSettings.Maximum, minValue = formSettings.InteliGiveSettings.Minimum;

    const liftVals = recurringLiftValue(seedAmt, formSettings.InteliGiveSettings.LiftRate, maxValue, minValue, maxValue, 1);

    const liftData = {
        ...liftVals,
        minAmountVal: formSettings.InteliGiveSettings.Minimum,
        maxAmountVal: formSettings.InteliGiveSettings.Maximum,
        defaultSelection: formSettings.InteliGiveSettings.DefaultSelection,
        trackingVal: formSettings.InteliGiveSettings.TrackingOption === 1 ? formSettings.OrganizationID : 0
    };

    sessionStorage.setItem('boostUp', JSON.stringify(liftData));
    sessionStorage.setItem('boostClicked', true);
    sessionStorage.setItem('seedAmount', seedAmt);

    return liftData;
};


/**
 * Formats stream name with donor information and amount.
 * Safely handles missing firstName and lastName properties.
 *
 * @param {string} format - Format string with placeholders
 * @param {Object} item - Donation item with donor information
 * @param {string} item.firstName - Donor's first name (optional)
 * @param {string} item.lastName - Donor's last name (optional)
 * @param {string} currency - Currency symbol
 * @param {boolean} hasIntelligive - Whether intelligent giving is enabled
 * @returns {string} Formatted stream name
 */
const usFormatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 });

export const formatStreamName = (format, item, currency, hasIntelligive) => {
    // Safely get firstName and lastName with fallbacks
    const firstName = item.firstName || '';
    const lastName = item.lastName || '';

    return format
        .replaceAll('{Patron_First_Name}', firstName)
        .replaceAll('{Patron_Last_Name}', lastName)
        .replaceAll('{Patron_First_Initial}', firstName.charAt(0) || '')
        .replaceAll('{Patron_Last_Initial}', lastName.charAt(0) || '')
        .replaceAll('{Amount}', `${hasIntelligive ? formatStreamAmount(format, item, currency, hasIntelligive) : ''}`)
        .replaceAll('{Comments}', '');
};


/**
 * Formats stream amount with currency and periodicity.
 *
 * @param {string} format - Format string
 * @param {Object} item - Donation item
 * @param {string} currency - Currency symbol
 * @param {boolean} hasIntelligive - Whether intelligent giving is enabled
 * @returns {string} Formatted amount string
 */
export function formatStreamAmount(format, item, currency, hasIntelligive) {

    if (format.includes('{Amount}')) {
        const formattedAmount = usFormatter.format(removedecimal(item.amount));
        const periodicity = item.periodicity && hasIntelligive && item.periodicity !== 'One Time Only'
            ? `/${item.periodicity}`
            : '';

        return `${currency}${formattedAmount}${periodicity}`;
    }

}

/**
 * Formats stream comments from donation item.
 *
 * @param {string} format - Format string
 * @param {Object} item - Donation item with comments
 * @returns {string} Formatted comments or empty string
 */
export const formatStreamComments = (format, item) => {
    if (format.includes('{Comments}')) {
        return item.comments ? item.comments : '';
    }

};

/**
 * Formats success message with donor and transaction information.
 * Safely handles missing first_name and last_name properties.
 *
 * @param {string} message - Message template with placeholders
 * @param {Object} data - Donor data
 * @param {string} data.first_name - Donor's first name (optional)
 * @param {string} data.last_name - Donor's last name (optional)
 * @param {number} total - Total donation amount
 * @param {string} orderNumber - Transaction order number
 * @param {string} currency - Currency symbol
 * @returns {string} Formatted success message
 */
export const formatSuccessMessage = (message, data, total, ...rest) => {
    const [ orderNumber, currency ] = rest;

    // Safely get first_name and last_name with fallbacks
    const firstName = data.first_name || '';
    const lastName = data.last_name || '';

    return message
        .replaceAll('{Patron_First_Name}', firstName)
        .replaceAll('{Patron_Last_Name}', lastName)
        .replaceAll('{Patron_First_Initial}', firstName.charAt(0) || '')
        .replaceAll('{Patron_Last_Initial}', lastName.charAt(0) || '')
        .replaceAll('{Amount}', `${currency}${usFormatter.format(removedecimal(total))}`)
        .replaceAll('{Order_Number}', orderNumber)
        .replaceAll('{Transaction_Result}', 'Transaction processed successfully');
};

/**
 * Gets display label for recurring payment options.
 *
 * @param {string} key - Recurring payment key
 * @returns {string} User-friendly display label
 */
export const cnpGetDisplayLabelForRecurring = key => {
    if (key) {
        switch (key) {
        case 'One Time Only':
            return 'One Time';
        case 'One Time':
            return 'One Time';
        case 'Week':
            return 'Weekly';
        case '2 Weeks':
            return '2 Weeks';
        case 'Month':
            return 'Monthly';
        case '2 Months':
            return '2 Months';
        case 'Quarter':
            return 'Quarterly';
        case '6 Months':
            return '6 Months';
        case 'Year':
            return 'Yearly';
        default:
            return key;
        }
    } else {
        return '';
    }
};

/**
 * Filters donation buttons to show maximum of 6 (5 preset + 1 open amount),
 * or maximum of 5 (4 preset + 1 open amount) when boost is active.
 *
 * @param {Array} array - Array of donation buttons
 * @returns {Array} Filtered array with maximum buttons
 */
export const filterButtonsToShowSix = array => {
    const isBoost = Boolean(
        typeof sessionStorage !== 'undefined'
        && (sessionStorage.getItem('boostClicked') === 'true'
            || sessionStorage.getItem('seedAmount')
            || sessionStorage.getItem('currentBoostStep')
            || sessionStorage.getItem('boostUp'))
    );

    const maxActive = isBoost ? 4 : 5;
    const activeButtons = array.filter(btn => btn.Type !== 'Open');

    const filteredButtons = activeButtons.length > maxActive
        ? activeButtons.slice(0, maxActive)
        : activeButtons;

    const openButton = array.find(btn => btn.Type === 'Open');

    if (openButton) {
        filteredButtons.push(openButton);
    }

    return filteredButtons;
};

/**
 * Detects the operating system of the user's device.
 *
 * @returns {string} Operating system name ("MacOS", "iOS", "Windows", "Android", "Linux", or "unknown")
 */
export const getOS = () => {
    let os = 'unknown';
    const platform = window.navigator?.userAgentData?.platform || window.navigator.platform;
    const userAgent = window.navigator.userAgent;
    const macosPlatforms = [ 'Macintosh', 'MacIntel', 'MacPPC', 'Mac68K' ];
    const windowsPlatforms = [ 'Win32', 'Win64', 'Windows', 'WinCE' ];
    const iosPlatforms = [ 'iPhone', 'iPad', 'iPod' ];

    if (macosPlatforms.indexOf(platform) !== -1) {
        os = 'MacOS';
    } else if (iosPlatforms.indexOf(platform) !== -1) {
        os = 'iOS';
    } else if (windowsPlatforms.indexOf(platform) !== -1) {
        os = 'Windows';
    } else if (/Android/.test(userAgent)) {
        os = 'Android';
    } else if (/Linux/.test(platform)) {
        os = 'Linux';
    }

    return os;

};

/**
 * Parses checkbox string into object with numeric values.
 *
 * @param {string} checkboxString - Semicolon-separated checkbox string (e.g., "SPM-1;SPS-0")
 * @returns {Object} Object with checkbox keys and numeric values
 */
export const parseCheckboxString = checkboxString => checkboxString.split(';').reduce((acc, item) => {
    const [ key, value ] = item.split('-');

    acc[key] = value === '1' ? 1 : 0;

    return acc;
}, {});

/**
 * IntelliBoost functionality starts here.
 * Converts periodicity keys to user-friendly names for IntelliBoost.
 *
 * @param {string} key - Periodicity key
 * @returns {Promise<string>} User-friendly periodicity name
 */
const getPeriodicityKeys = async key => {
    switch (key) {
    case 'OneTime':
        return 'One Time Only';
    case 'Weekly':
        return 'Week';
    case 'TwoWeeks':
        return '2 Weeks';
    case 'Monthly':
        return 'Month';
    case 'TwoMonths':
        return '2 Months';
    case 'Quarterly':
        return 'Quarter';
    case 'SixMonths':
        return '6 Months';
    case 'Yearly':
        return 'Year';
    default:
        return key;
    }
};


/**
 * Fetches IntelliBoost XML data for button configurations.
 *
 * @returns {Promise<Array>} Array of button set configurations
 */
const fetchIntelliBoostXMLData = async () => {
    try {
        const xmlText = await fetchIntelliBoostXml();

        if (!xmlText) {
            return [];
        }

        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlText, 'application/xml');
        const buttonSets = [ ...xmlDoc.querySelectorAll('ButtonSet') ];

        return buttonSets.map(set => {
            const obj = {
                seed: parseInt(set.getAttribute('seed'), 10),
                step: parseInt(set.getAttribute('step'), 10)
            };

            [ ...set.children ].forEach(child => {
                obj[child.tagName] = child.textContent.split(',').map(Number);
            });

            return obj;
        });
    } catch (err) {
        logger.error('Error fetching IntelliBoost XML:', err);

        return [];
    }
};

/**
 * Gets the next step values for IntelliBoost donation suggestions.
 *
 * @param {Array} paymentOptions - Current payment options
 * @param {number} amount - Seed amount for calculations
 * @returns {Promise<Array|null>} Updated payment options with next step values or null
 */
export const getNextStepValue = async (paymentOptions, amount) => {
    try {
        let cleanAmount = typeof amount === 'number'
            ? amount
            : parseFloat(String(amount || '').replace(/[^0-9.]/g, ''));

        // If no valid amount passed, attempt to find preselected amount from paymentOptions
        if (isNaN(cleanAmount) || cleanAmount <= 0) {
            const firstOption = (paymentOptions || [])[0];
            const preItem = firstOption?.Value?.DonationItemList?.find(
                i => i.DefaultSelection === true || i.DefaultSelection === 'true' || i.DefaultSelection === 'True'
            );

            if (preItem?.DefaultValue > 0) {
                cleanAmount = Number(preItem.DefaultValue);
            }
        }

        const seedamount = Math.floor(cleanAmount);
        const buttonSets = await fetchIntelliBoostXMLData();

        if (!buttonSets || !buttonSets.length) {
            return paymentOptions;
        }

        const sorted = [ ...buttonSets ].sort((a, b) => a.seed - b.seed);

        const isBoostClicked = sessionStorage.getItem('boostClicked') === 'true';
        const storedStep = sessionStorage.getItem('currentBoostStep');
        const prevStep = storedStep === null ? null : parseInt(storedStep, 10);

        let found = null;

        if (isBoostClicked) {
            // Boost button was clicked: calculate next step
            let target = null;

            if (!isNaN(seedamount) && seedamount > 0) {
                // Find the LAST set with the same seed to skip duplicates
                target = [ ...sorted ].reverse().find(d => d.seed === seedamount);

                if (!target) {
                    target = sorted.reduce((prev, curr) => {
                        if (Math.abs(curr.seed - seedamount) < Math.abs(prev.seed - seedamount)) {
                            return curr;
                        }

                        return prev;
                    });
                }
            }

            let nextStep = 1;

            if (target) {
                nextStep = target.step + 1;
            } else if (prevStep !== null && !isNaN(prevStep)) {
                nextStep = prevStep;
            }

            found = sorted.find(d => d.step === nextStep) || sorted[sorted.length - 1];

            if (found) {
                sessionStorage.setItem('currentBoostStep', String(found.step));
                if (!isNaN(seedamount) && seedamount > 0) {
                    sessionStorage.setItem('seedAmount', String(seedamount));
                }
            }
            sessionStorage.setItem('boostClicked', 'false');
        } else {
            // Re-rendering or donating again: maintain the boosted step
            if (prevStep !== null && !isNaN(prevStep)) {
                found = sorted.find(d => d.step === prevStep);
            }

            if (!found && !isNaN(seedamount) && seedamount > 0) {
                let target = [ ...sorted ].reverse().find(d => d.seed === seedamount);

                if (!target) {
                    target = sorted.reduce((prev, curr) => {
                        if (Math.abs(curr.seed - seedamount) < Math.abs(prev.seed - seedamount)) {
                            return curr;
                        }

                        return prev;
                    });
                }

                const nextStep = target ? target.step + 1 : 1;

                found = sorted.find(d => d.step === nextStep) || sorted[sorted.length - 1];
                if (found) {
                    sessionStorage.setItem('currentBoostStep', String(found.step));
                }
            }
        }

        if (!found) {
            return paymentOptions;
        }

        // Map its keys to friendly ones
        const mapped = {};

        for (const [ key, value ] of Object.entries(found)) {
            if ([ 'seed', 'step' ].includes(key)) {
                mapped[key] = value;
            } else {
                const friendlyKey = await getPeriodicityKeys(key); // convert

                mapped[friendlyKey] = value;
            }
        }

        return paymentOptions.map(option => {
            const key = option.Key; // e.g. "One Time Only", "Month", etc.

            if (mapped[key] && Array.isArray(mapped[key]) && option.Value?.DonationItemList) {
                const newValues = mapped[key];

                const originalRegularItems = option.Value.DonationItemList.filter(
                    i => i.DonationFor !== 'Other' && i.Type !== 'Open'
                );

                // Preserve the exact preselected position from the original list
                let preselectedRegularIdx = originalRegularItems.findIndex(
                    i => i.DefaultSelection === true || i.DefaultSelection === 'true' || i.DefaultSelection === 'True'
                );

                if (preselectedRegularIdx === -1 && !isNaN(seedamount) && seedamount > 0) {
                    preselectedRegularIdx = originalRegularItems.findIndex(
                        i => Number(i.DefaultValue) === Number(seedamount)
                    );
                }

                if (preselectedRegularIdx === -1) {
                    preselectedRegularIdx = 0;
                }

                // C&P XML values in Buttons_Recurring.xml are always in descending order (highest to lowest).
                // Check if the existing regular items are ascending or descending to match their layout.
                const isAscending = originalRegularItems.length < 2
                    || Number(originalRegularItems[0]?.DefaultValue || 0)
                        <= Number(originalRegularItems[originalRegularItems.length - 1]?.DefaultValue || 0);

                const sortedValues = [ ...newValues ].sort(
                    isAscending ? (a, b) => a - b : (a, b) => b - a
                );

                if (preselectedRegularIdx >= sortedValues.length) {
                    preselectedRegularIdx = sortedValues.length - 1;
                }

                let fixedIdx = 0;
                const updatedItems = [];

                for (const item of option.Value.DonationItemList) {
                    if (item.DonationFor === 'Other' || item.Type === 'Open') {
                        updatedItems.push({
                            ...item,
                            DefaultSelection: false
                        });
                    } else if (fixedIdx < sortedValues.length) {
                        // Remove the 5th (or beyond) donation amount button when boost is active,
                        // since boosted values from IntelliBoost only provide sortedValues.length (4) amounts.
                        const val = sortedValues[fixedIdx];
                        const isPreselected = fixedIdx === preselectedRegularIdx;

                        fixedIdx++;

                        updatedItems.push({
                            ...item,
                            DefaultValue: val ?? item.DefaultValue,
                            ItemAmount: val ?? item.ItemAmount,
                            DefaultValueDisplay: typeof val === 'undefined' ? item.DefaultValueDisplay : Number(val).toFixed(2),
                            DefaultSelection: isPreselected
                        });
                    }
                }

                // Ensure at least one regular item has DefaultSelection = true
                const hasSelected = updatedItems.some(i => i.DefaultSelection);

                if (!hasSelected && updatedItems.length) {
                    const firstRegular = updatedItems.find(i => i.DonationFor !== 'Other' && i.Type !== 'Open');

                    if (firstRegular) {
                        firstRegular.DefaultSelection = true;
                    } else {
                        updatedItems[0].DefaultSelection = true;
                    }
                }

                return {
                    ...option,
                    Value: {
                        ...option.Value,
                        DonationItemList: updatedItems
                    }
                };
            }

            return {
                ...option,
                Value: {
                    ...option?.Value,
                    DonationItemList: option?.Value?.DonationItemList ? [ ...option.Value.DonationItemList ] : []
                }
            };
        });
    } catch (err) {
        logger.error('getNextStepValue error:', err);

        return paymentOptions;
    }
};


